import BaseAPI, { recoverFromNotFound, ROUTES, VERSIONS } from './BaseAPI'

const { VERSION_TWO } = VERSIONS
const { ROLES_CREATE_P2P_GROUP } = ROUTES

export default class RolesAPI extends BaseAPI {
  /**
   * POST /group - create a group
   * @param  {string} options.name - Group name
   * @param  {Boolean} options.replayHistory - Replay History
   * @param  {Array<string>} options.memberIds - Member IDs
   * @param  {string} options.createdBy - User ID or Role ID who is creating the group
   * @param  {string} options.organizationId - Organization ID
   * @return {Promise.<Object,Error>} - A promise with an object representing a group
   */
  async createP2PGroup({
    createdByUserId,
    name,
    replayHistory = false,
    memberIds = [],
    organizationId
  }) {
    const { version=VERSION_TWO } = super.getVersion(ROLES_CREATE_P2P_GROUP)
    const headers = {
      'TT-X-Organization-Key': organizationId,
    }
    const data = {
      'created_by': createdByUserId,
      'members': memberIds,
      'name': name,
      'replay_history': replayHistory,
    }

    const res = await this.httpClient.post('/:version/role_group', {
      urlParams: { version },
      headers,
      data,
    })

    const { conversation_id: conversationId, metadata } = res.data
    let { token } = res.data

    const group = {
      conversationId,
      memberIds,
      metadata,
      name,
      organizationId,
      token,
    }

    return group
  }

  @recoverFromNotFound()
  async find(id: string, organizationId: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    const res = await this.httpClient.get('/v2/role/:id', {
      urlParams: { id },
      headers
    })

    return res.data
  }

  async createRole({
    description,
    name,
    openAssignment,
    organizationId,
    ownerRequired,
    owners,
    replayHistory,
    tagColor,
    tagName,
    tagToken,
    escalationPolicy,
  }: {
    description?: ?string,
    name: string,
    openAssignment: boolean,
    organizationId: string,
    ownerRequired?: ?boolean,
    owners?: ?Array<string>,
    replayHistory: string,
    tagColor?: ?string,
    tagName?: ?string,
    tagToken?: ?string,
    escalationPolicy?: ?Object,
  }) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    const data = {
      'display_name': name,
      'open_assignment': openAssignment ? '1' : '0',
      'replay_history': replayHistory,
    }

    if (description !== undefined) data['description'] = description
    if (escalationPolicy !== undefined) data['escalation_policy'] = escalationPolicy
    if (ownerRequired !== undefined) data['owner_required'] = ownerRequired ? '1' : '0'
    if (owners !== undefined) data['owners'] = owners
    if (tagColor !== undefined) data['tag_color'] = tagColor
    if (tagToken !== undefined) data['tag_id'] = tagToken
    if (tagName !== undefined) data['tag_name'] = tagName

    const res = await this.httpClient.post('/v2/role', {
      headers,
      data,
    })
    res.token = res.headers['tt-x-role-token']

    return res
  }

  async updateRole(token: string, {
    description,
    name,
    openAssignment,
    organizationId,
    ownerRequired,
    owners,
    replayHistory,
    tagColor,
    tagName,
    tagToken,
    escalationPolicy,
  }: {
    description?: ?string,
    name?: ?string,
    openAssignment?: ?boolean,
    organizationId: string,
    ownerRequired?: ?boolean,
    owners?: ?Array<string>,
    replayHistory?: ?string,
    tagColor?: ?string,
    tagName?: ?string,
    tagToken?: ?string,
    escalationPolicy?: ?Object,
  }) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    const data = {}

    if (description !== undefined) data['description'] = description
    if (name !== undefined) data['display_name'] = name
    if (escalationPolicy !== undefined) data['escalation_policy'] = escalationPolicy
    if (openAssignment !== undefined) data['open_assignment'] = openAssignment ? '1' : '0'
    if (ownerRequired !== undefined) data['owner_required'] = ownerRequired ? '1' : '0'
    if (owners !== undefined) data['owners'] = owners
    if (replayHistory !== undefined) data['replay_history'] = replayHistory
    if (tagColor !== undefined) data['tag_color'] = tagColor
    if (tagToken !== undefined) data['tag_id'] = tagToken
    if (tagName !== undefined) data['tag_name'] = tagName

    const res = await this.httpClient.put('/v2/role/:token', {
      urlParams: { token },
      headers,
      data,
    })
    res.token = token

    return res
  }

  async deleteRole(organizationId: string, token: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    return await this.httpClient.del('/v2/role/:token', {
      urlParams: { token },
      headers
    })
  }

  @recoverFromNotFound()
  async findSavedRoles(userId: string, organizationId: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    const res = await this.httpClient.get('/v2/user/:userId/saved_roles', {
      urlParams: { userId },
      headers
    })

    return res.data
  }

  async saveRole(roleId: string, userId: string, organizationId: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    return await this.httpClient.post('/v2/user/:userId/saved_roles', {
      urlParams: { userId },
      data: {
        role_id: roleId,
      },
      headers
    })
  }

  async removeSavedRole(roleId: string, userId: string, organizationId: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    return await this.httpClient.del('/v2/user/:userId/saved_roles/:roleId', {
      urlParams: { userId, roleId },
      headers
    })
  }

  async optIn(roleId: string, userId: string, organizationId: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    return await this.httpClient.post('/v2/role/:roleId/members/:userId', {
      urlParams: { roleId, userId },
      headers
    })
  }

  async optOut(roleId: string, userId: string, organizationId: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    return await this.httpClient.del('/v2/role/:roleId/members/:userId', {
      urlParams: { roleId, userId },
      headers
    })
  }

  async saveAwayResponse(roleId: string, organizationId: string, status: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    return await this.httpClient.post('/v2/role/:roleId/away', {
      urlParams: {roleId},
      headers,
      data: {
        status
      }
    })
  }

  async removeAwayResponse(roleId: string, organizationId: string) {
    const headers = { 'TT-X-Organization-Key': organizationId }
    return await this.httpClient.del('/v2/role/:roleId/away', {
      urlParams: {roleId},
      headers
    })
  }
}
